import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import GroupIcon from "@material-ui/icons/Group";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  chatHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
  },

  chatHeaderTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    flex: 1,
  },

  chatHeaderParticipants: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },

  messageList: {
    position: "relative",
    overflowY: "auto",
    flex: 1,
    height: "100%",
    padding: theme.spacing(1, 0),
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.chatlist,
  },

  inputArea: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },

  input: {
    padding: theme.spacing(1.5, 2),
    fontSize: "0.875rem",
  },

  buttonSend: {
    margin: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },

  // Mensagem recebida
  boxLeft: {
    padding: "8px 12px 5px",
    margin: "6px 48px 6px 12px",
    position: "relative",
    backgroundColor: theme.palette.type === "light" ? "#e8edf8" : "#2a3a52",
    maxWidth: 320,
    borderRadius: 12,
    borderBottomLeftRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
  },

  // Mensagem enviada
  boxRight: {
    padding: "8px 12px 5px",
    margin: "6px 12px 6px auto",
    position: "relative",
    backgroundColor: "#2D7BE8",
    maxWidth: 320,
    borderRadius: 12,
    borderBottomRightRadius: 2,
    textAlign: "right",
  },

  senderName: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: 2,
  },

  messageText: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },

  messageTextOwn: {
    fontSize: "0.875rem",
    color: "#fff",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },

  timestamp: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    marginTop: 3,
  },

  timestampOwn: {
    fontSize: "0.65rem",
    color: "rgba(255,255,255,0.7)",
    marginTop: 3,
  },
}));

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();

  const [contentMessage, setContentMessage] = useState("");

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser.unreads > 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (contentMessage.trim() !== "") {
        handleSendMessage(contentMessage);
        setContentMessage("");
      }
    }
  };

  const participantNames = chat?.users
    ?.filter((u) => u.userId !== user.id)
    .map((u) => u.user?.name)
    .filter(Boolean)
    .join(", ");

  return (
    <Paper className={classes.mainContainer}>
      {/* Cabeçalho da conversa */}
      {chat && (
        <div className={classes.chatHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.chatHeaderTitle} noWrap>
              {chat.title}
            </Typography>
            {participantNames && (
              <Typography className={classes.chatHeaderParticipants} noWrap>
                {participantNames}
              </Typography>
            )}
          </div>
          <GroupIcon style={{ fontSize: 18, opacity: 0.35 }} />
        </div>
      )}

      {/* Lista de mensagens */}
      <div onScroll={handleScroll} className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            const isOwn = item.senderId === user.id;
            return (
              <Box key={key} className={isOwn ? classes.boxRight : classes.boxLeft}>
                {!isOwn && (
                  <Typography className={classes.senderName}>
                    {item.sender.name}
                  </Typography>
                )}
                <Typography className={isOwn ? classes.messageTextOwn : classes.messageText}>
                  {item.message}
                </Typography>
                <Typography
                  className={isOwn ? classes.timestampOwn : classes.timestamp}
                  display="block"
                >
                  {datetimeToClient(item.createdAt)}
                </Typography>
              </Box>
            );
          })}
        <div ref={baseRef} />
      </div>

      {/* Input */}
      <div className={classes.inputArea}>
        <FormControl variant="outlined" fullWidth>
          <Input
            multiline
            rowsMax={4}
            value={contentMessage}
            onKeyDown={handleKeyDown}
            onChange={(e) => setContentMessage(e.target.value)}
            className={classes.input}
            placeholder="Mensagem... (Shift+Enter para nova linha)"
            disableUnderline
            endAdornment={
              <InputAdornment position="end">
                <Tooltip title="Enviar (Enter)">
                  <span>
                    <IconButton
                      onClick={() => {
                        if (contentMessage.trim() !== "") {
                          handleSendMessage(contentMessage);
                          setContentMessage("");
                        }
                      }}
                      disabled={!contentMessage.trim()}
                      className={classes.buttonSend}
                      size="small"
                    >
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            }
          />
        </FormControl>
      </div>
    </Paper>
  );
}
